import { cn } from 'lib/utils'
import { useMemo } from 'react'

// A static, decorative mock of the weekly calendar a live plan produces. Purely
// visual — no data, no store, no interactivity — so the autopilot empty state
// can preview the payoff (a full week of scheduled posts) instead of a bare
// CTA. The caller renders it blurred + masked behind the foreground content.
//
// It mirrors WeekGrid's structure (7-column grid, mono weekday header, per-day
// date label, post tiles) closely enough to read as the real thing once
// softened — but deliberately doesn't import CreativeCard or the queue store so
// it stays cheap and free of signed-media fetches. It stretches to fill its
// container (h-full) so it reads as a whole calendar rather than a top strip.

// Which columns hold a mock post (others render the dashed empty slot), and the
// per-tile thumbnail gradient so the blurred blobs read as distinct posts
// rather than one repeated tile. Acid uses the live token; the rest are static
// accent hints. Arbitrary `var()` / hex stops are used because the shim color
// utilities (bg-acid etc.) don't register as gradient color-stops.
const GHOST_DAYS: (string | null)[] = [
  'from-[var(--acid)]/45 to-[var(--bg-3)]',
  null,
  'from-[#7dd3fc]/40 to-[var(--bg-3)]',
  'from-[#f0abfc]/35 to-[var(--bg-3)]',
  null,
  'from-[var(--acid)]/35 to-[var(--bg-3)]',
  'from-[#fda4af]/35 to-[var(--bg-3)]',
]

export function WeekGridGhost({ className }: { className?: string }) {
  // Label the upcoming 7 days so the preview matches what a real plan shows.
  // Recomputed once per mount — fine for a decorative element.
  const days = useMemo(() => {
    const base = new Date()
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base)
      d.setDate(base.getDate() + i)
      return {
        weekday: d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
        date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      }
    })
  }, [])

  return (
    <div className={cn('flex h-full flex-col border-x border-line', className)}>
      <div className="grid grid-cols-7 border-y border-line">
        {days.map(day => (
          <div
            key={`ghost-wkd-${day.weekday}-${day.date}`}
            className="mono border-r border-line px-2 py-2 text-center text-[10px] tracking-[0.18em] text-paper-faint uppercase last:border-r-0"
          >
            {day.weekday}
          </div>
        ))}
      </div>

      <div className="grid flex-1 auto-rows-fr grid-cols-7">
        {days.map((day, i) => (
          <div
            key={`ghost-day-${day.date}`}
            className="flex min-h-0 min-w-0 flex-col border-r border-b border-line bg-bg-2 last:border-r-0"
          >
            <div className="border-b border-line px-3 py-2 text-center">
              <p className="text-[13px] font-medium text-paper">{day.date}</p>
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-2 p-2">
              {GHOST_DAYS[i] ? (
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-line bg-bg-2">
                  <div className={cn('min-h-0 flex-1 bg-gradient-to-br', GHOST_DAYS[i])} />
                  <div className="shrink-0 space-y-1.5 p-2">
                    <div className="h-2 w-3/4 rounded bg-bg-3" />
                    <div className="flex items-center gap-1.5">
                      <div className="h-3 w-3 rounded-full bg-bg-3" />
                      <div className="h-2 w-1/3 rounded bg-bg-3" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="min-h-0 w-full flex-1 rounded-[12px] border border-dashed border-line bg-bg-3/30" />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
